import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Mail, AlertTriangle, Shield, Upload, Link2, Paperclip,
  ChevronRight, ExternalLink, Clock, User, FileWarning,
  CheckCircle, XCircle, AlertCircle, Search, Filter,
  RefreshCw, Settings, Cloud, Loader2, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Risk badge colors
const getRiskBadge = (classification) => {
  const styles = {
    critical: 'bg-red-500/20 text-red-400 border-red-500/30',
    high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    low: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    safe: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  };
  return styles[classification] || styles.medium;
};

const getStatusBadge = (status) => {
  const styles = {
    quarantined: 'bg-red-500/20 text-red-400',
    flagged: 'bg-yellow-500/20 text-yellow-400',
    delivered: 'bg-emerald-500/20 text-emerald-400',
  };
  return styles[status] || '';
};

export default function EmailThreats() {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [analyzeDialogOpen, setAnalyzeDialogOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  
  // Form state for email analysis
  const [emailForm, setEmailForm] = useState({
    subject: '',
    sender: '',
    body_text: '',
    urls: ''
  });

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [eventsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/threats/emails`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/email-gateway/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);
      
      setEvents(eventsRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch email data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeEmail = async () => {
    if (!emailForm.subject && !emailForm.sender && !emailForm.body_text) {
      toast.error('Please provide email content to analyze');
      return;
    }
    
    setAnalyzing(true);
    setAnalysisResult(null);
    
    try {
      const response = await axios.post(
        `${API}/api/email-gateway/analyze`,
        {
          subject: emailForm.subject,
          sender: emailForm.sender,
          body_text: emailForm.body_text,
          urls: emailForm.urls ? emailForm.urls.split('\n').filter(u => u.trim()) : []
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAnalysisResult(response.data);
      toast.success('Email analyzed successfully');
      fetchData(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.eml') && !file.name.endsWith('.txt')) {
      toast.error('Please upload a .eml or .txt file');
      return;
    }
    
    setAnalyzing(true);
    setAnalysisResult(null);
    
    try {
      const content = await file.text();
      
      const response = await axios.post(
        `${API}/api/email-gateway/analyze-raw`,
        content,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'text/plain'
          }
        }
      );
      
      setAnalysisResult(response.data);
      toast.success('Email file analyzed successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const resetForm = () => {
    setEmailForm({ subject: '', sender: '', body_text: '', urls: '' });
    setAnalysisResult(null);
  };

  return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-white">Email Security Gateway</h1>
            <p className="text-slate-400">Advanced email threat detection and analysis</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="border-slate-700"
              onClick={fetchData}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Dialog open={analyzeDialogOpen} onOpenChange={setAnalyzeDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <Search className="w-4 h-4 mr-2" />
                  Analyze Email
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl bg-slate-900 border-slate-800 max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-white">Analyze Suspicious Email</DialogTitle>
                  <DialogDescription className="text-slate-400">
                    Submit email content for security analysis or upload a .eml file
                  </DialogDescription>
                </DialogHeader>
                
                <Tabs defaultValue="manual" className="mt-4">
                  <TabsList className="bg-slate-800">
                    <TabsTrigger value="manual">Manual Entry</TabsTrigger>
                    <TabsTrigger value="upload">Upload File</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="manual" className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-300">Sender Email</Label>
                        <Input
                          placeholder="sender@example.com"
                          value={emailForm.sender}
                          onChange={(e) => setEmailForm({...emailForm, sender: e.target.value})}
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">Subject</Label>
                        <Input
                          placeholder="Email subject line"
                          value={emailForm.subject}
                          onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-slate-300">Email Body</Label>
                      <Textarea
                        placeholder="Paste the email content here..."
                        value={emailForm.body_text}
                        onChange={(e) => setEmailForm({...emailForm, body_text: e.target.value})}
                        className="bg-slate-800 border-slate-700 text-white min-h-[150px]"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-slate-300">URLs in Email (one per line)</Label>
                      <Textarea
                        placeholder="http://example.com/link1&#10;http://example.com/link2"
                        value={emailForm.urls}
                        onChange={(e) => setEmailForm({...emailForm, urls: e.target.value})}
                        className="bg-slate-800 border-slate-700 text-white min-h-[80px]"
                      />
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="upload" className="mt-4">
                    <div className="border-2 border-dashed border-slate-700 rounded-lg p-8 text-center">
                      <Upload className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                      <p className="text-slate-300 mb-2">Upload .eml file for analysis</p>
                      <p className="text-slate-500 text-sm mb-4">Drag and drop or click to browse</p>
                      <Input
                        type="file"
                        accept=".eml,.txt"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="eml-upload"
                      />
                      <label htmlFor="eml-upload">
                        <Button variant="outline" className="border-slate-700" asChild>
                          <span>Choose File</span>
                        </Button>
                      </label>
                    </div>
                  </TabsContent>
                </Tabs>
                
                {/* Analysis Result */}
                {analysisResult && (
                  <div className="mt-6 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      Analysis Result
                    </h3>
                    
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="text-center p-3 bg-slate-900 rounded-lg">
                        <p className="text-3xl font-bold text-white">{analysisResult.risk_score}</p>
                        <p className="text-xs text-slate-400">Risk Score</p>
                      </div>
                      <div className="text-center p-3 bg-slate-900 rounded-lg">
                        <Badge className={getRiskBadge(analysisResult.classification)}>
                          {analysisResult.classification?.toUpperCase()}
                        </Badge>
                        <p className="text-xs text-slate-400 mt-1">Classification</p>
                      </div>
                      <div className="text-center p-3 bg-slate-900 rounded-lg">
                        <Badge className={getStatusBadge(analysisResult.status)}>
                          {analysisResult.status?.toUpperCase()}
                        </Badge>
                        <p className="text-xs text-slate-400 mt-1">Status</p>
                      </div>
                    </div>
                    
                    {/* Indicators */}
                    {analysisResult.indicators?.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm text-slate-400 mb-2">Threat Indicators:</p>
                        <div className="flex flex-wrap gap-2">
                          {analysisResult.indicators.map((ind, i) => (
                            <Badge key={i} variant="outline" className="border-red-500/30 text-red-400 text-xs">
                              {ind}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Recommendations */}
                    {analysisResult.recommendations?.length > 0 && (
                      <div>
                        <p className="text-sm text-slate-400 mb-2">Recommendations:</p>
                        <ul className="space-y-1">
                          {analysisResult.recommendations.map((rec, i) => (
                            <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                              <AlertCircle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                
                <DialogFooter className="mt-6">
                  <Button variant="outline" onClick={resetForm} className="border-slate-700">
                    Reset
                  </Button>
                  <Button
                    onClick={handleAnalyzeEmail}
                    disabled={analyzing}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {analyzing ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyzing...</>
                    ) : (
                      <><Search className="w-4 h-4 mr-2" />Analyze Email</>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Total Analyzed</p>
                  <p className="text-2xl font-bold text-white">{stats?.total_analyzed || events.length}</p>
                </div>
                <Mail className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Quarantined</p>
                  <p className="text-2xl font-bold text-red-400">{stats?.by_status?.quarantined || 0}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Flagged</p>
                  <p className="text-2xl font-bold text-yellow-400">{stats?.by_status?.flagged || 0}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Threat Rate</p>
                  <p className="text-2xl font-bold text-white">{stats?.threat_rate || 0}%</p>
                </div>
                <Shield className="w-8 h-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Email Events Table */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white">Recent Email Analysis</CardTitle>
            <CardDescription className="text-slate-400">View and manage analyzed emails</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-12">
                <Mail className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No email events yet</p>
                <p className="text-slate-500 text-sm">Analyze suspicious emails using the button above</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-800">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Subject</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Sender</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Risk</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Time</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((event) => (
                      <tr key={event.id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-slate-500" />
                            <span className="text-white truncate max-w-[200px]">
                              {event.subject || 'No Subject'}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-slate-300 truncate max-w-[180px]">
                          {event.sender || event.sender_email || 'Unknown'}
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={getRiskBadge(event.classification || event.threat_type)}>
                            {event.risk_score || 0}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={getStatusBadge(event.status || 'delivered')}>
                            {(event.status || 'analyzed').toUpperCase()}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-slate-400 text-sm">
                          {event.timestamp ? new Date(event.timestamp).toLocaleString() : '-'}
                        </td>
                        <td className="py-3 px-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedEvent(event)}
                            className="text-slate-400 hover:text-white"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Event Detail Dialog */}
        <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
          <DialogContent className="max-w-2xl bg-slate-900 border-slate-800">
            <DialogHeader>
              <DialogTitle className="text-white">Email Analysis Details</DialogTitle>
            </DialogHeader>
            
            {selectedEvent && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-400">Subject</p>
                    <p className="text-white">{selectedEvent.subject || 'No Subject'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Sender</p>
                    <p className="text-white">{selectedEvent.sender || selectedEvent.sender_email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Risk Score</p>
                    <Badge className={getRiskBadge(selectedEvent.classification)}>
                      {selectedEvent.risk_score} - {selectedEvent.classification?.toUpperCase()}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Status</p>
                    <Badge className={getStatusBadge(selectedEvent.status)}>
                      {selectedEvent.status?.toUpperCase()}
                    </Badge>
                  </div>
                </div>
                
                {selectedEvent.indicators?.length > 0 && (
                  <div>
                    <p className="text-sm text-slate-400 mb-2">Threat Indicators</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedEvent.indicators.map((ind, i) => (
                        <Badge key={i} variant="outline" className="border-red-500/30 text-red-400">
                          {ind}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-slate-800">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{selectedEvent.phishing_score || 0}</p>
                    <p className="text-xs text-slate-400">Phishing Score</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{selectedEvent.malware_score || 0}</p>
                    <p className="text-xs text-slate-400">Malware Score</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{selectedEvent.sender_reputation || 'N/A'}</p>
                    <p className="text-xs text-slate-400">Sender Reputation</p>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
  );
}
